import React, { useMemo } from "react"
import classNames from "classnames"

import OperatorV2 from "@/components/OperatorV2"
import Top3OperatorsV2 from "@/components/Top3OperatorsV2"
import { useTranslation } from "@/locales"
import { parseColorString } from "@/utils/i18n"
import { IServerSummary } from "../../pages/Record"
import Clear from "../Clear"
import Copper from "../Copper"
import Count from "../Count"
import Item from "../Item"
import PartTitle from "../PartTitle"
import SectionTitle from "../SectionTitle"
import Wrath from "../Wrath"

import styles from "./index.module.less"

interface IProps {
    data?: IServerSummary | null
    className?: string
    style?: React.CSSProperties
}

const ServerSummary: React.FC<IProps> = (props) => {
    const { data, className, style } = props
    const { t } = useTranslation()

    const endingCount = useMemo(() => {
        if (__ROGUE_5_DLC === 1) {
            return 4
        }

        return 3
    }, [])

    return (
        <div
            className={classNames(
                styles.serverSummary,
                data && styles.show,
                className
            )}
            style={style}
        >
            <PartTitle
                subLabel={t("server.1.title.sub")}
                label={t("server.1.title")}
            />
            <section className={styles.clear}>
                <SectionTitle data={parseColorString(t("clear"))} />
                <div className={styles.content}>
                    {Array.from({ length: endingCount }).map((_, index) => (
                        <Clear
                            key={index}
                            index={index + 1}
                            value={data?.endings?.[index + 1] || "0.00"}
                        />
                    ))}
                </div>
            </section>
            {data?.activeTool?.length ? (
                <section className={styles.tool}>
                    <SectionTitle data={parseColorString(t("tool"))} />
                    <div className={styles.content}>
                        {data.activeTool.map(({ id, name }, index) => (
                            <Item
                                key={index}
                                type="tool"
                                index={index + 1}
                                id={id}
                                name={name}
                            />
                        ))}
                    </div>
                </section>
            ) : null}
            {data?.relic?.length ? (
                <section className={styles.relic}>
                    <SectionTitle data={parseColorString(t("relic"))} />
                    <div className={styles.content}>
                        {data.relic.map(({ id, name }, index) => (
                            <Item
                                key={index}
                                type="relic"
                                index={index + 1}
                                id={id}
                                name={name}
                            />
                        ))}
                    </div>
                </section>
            ) : null}

            {Object.values(data?.character || {}).some((v) => v.length) ? (
                <>
                    <PartTitle
                        subLabel={t("server.2.title.sub")}
                        label={t("server.2.title")}
                    />
                    <section className={styles.operator}>
                        <SectionTitle data={parseColorString(t("operator"))} />
                        <div className={styles.content}>
                            {[
                                "PIONEER",
                                "WARRIOR",
                                "TANK",
                                "SNIPER",
                                "CASTER",
                                "MEDIC",
                                "SUPPORT",
                                "SPECIAL",
                            ].map((key) =>
                                data?.character?.[key].length ? (
                                    <Top3OperatorsV2
                                        key={key}
                                        className={styles.top3Operators}
                                        profession={key}
                                        operators={data?.character[key]}
                                    />
                                ) : null
                            )}
                        </div>
                    </section>
                </>
            ) : null}

            <PartTitle
                subLabel={t("server.3.title.sub")}
                label={t("server.3.title")}
            />
            {data?.wrath?.length ? (
                <section className={styles.wrath}>
                    <SectionTitle data={parseColorString(t("wrath"))} />
                    <div className={styles.content}>
                        {data.wrath.map(({ id, name }, index) => (
                            <Wrath
                                key={index}
                                index={index + 1}
                                id={id}
                                name={name}
                            />
                        ))}
                    </div>
                </section>
            ) : null}
            {data?.copper?.length ? (
                <section className={styles.copper}>
                    <SectionTitle data={parseColorString(t("copper"))} />
                    <div className={styles.content}>
                        {data.copper.map(({ id, name }, index) => (
                            <Copper key={index} id={id} name={name} />
                        ))}
                    </div>
                </section>
            ) : null}
            <section className={styles.ticket}>
                <SectionTitle data={parseColorString(t("ticket"))} />
                <div className={classNames(styles.content, styles.center)}>
                    <div className={styles.detail}>
                        <div className={styles.desc}>{t("ticket.desc")}</div>
                        <Count value={data?.ticket} />
                    </div>
                </div>
            </section>
            {data?.candle?.length ? (
                <section className={styles.candle}>
                    <SectionTitle data={parseColorString(t("candle"))} />
                    <div className={styles.content}>
                        {data.candle.map((char, index) => (
                            <div key={index} className={styles.item}>
                                <OperatorV2 data={char} />
                                <div className={styles.label} />
                            </div>
                        ))}
                    </div>
                </section>
            ) : null}
            {__ROGUE_5_DLC >= 1 && data?.expeditionGuide?.length ? (
                <section className={styles.expeditionGuide}>
                    <SectionTitle
                        data={parseColorString(t("expeditionGuide"))}
                    />
                    <div className={styles.content}>
                        {data.expeditionGuide.map((char, index) => (
                            <div key={index} className={styles.item}>
                                <OperatorV2 data={char} />
                                <div className={styles.label} />
                            </div>
                        ))}
                    </div>
                </section>
            ) : null}
            <section className={styles.costHp}>
                <SectionTitle data={parseColorString(t("costHp"))} />
                <div className={styles.content}>
                    <div className={styles.detail}>
                        <Count value={(data?.costHp || 0).toLocaleString()} />
                    </div>
                </div>
            </section>
            <section className={styles.nodePass}>
                <SectionTitle data={parseColorString(t("nodePass"))} />
                <div className={classNames(styles.content, styles.center)}>
                    <div className={styles.detail}>
                        <div className={styles.desc}>{t("nodePass.desc")}</div>
                        <Count value={data?.nodePass} />
                    </div>
                </div>
            </section>
            <section className={styles.fall}>
                <SectionTitle data={parseColorString(t("fall"))} />
                <div className={classNames(styles.content, styles.center)}>
                    <div className={styles.detail}>
                        <div className={styles.desc}>{t("fall.desc")}</div>
                        <Count value={data?.fall} />
                    </div>
                </div>
            </section>
            <section className={styles.rotate}>
                <SectionTitle data={parseColorString(t("rotate"))} />
                <div className={classNames(styles.content, styles.center)}>
                    <div className={styles.detail}>
                        <div className={styles.desc}>{t("rotate.desc")}</div>
                        <Count value={data?.rotate} />
                    </div>
                </div>
            </section>
            <section className={styles.gold}>
                <SectionTitle data={parseColorString(t("gold"))} />
                <div className={classNames(styles.content, styles.center)}>
                    <div className={styles.detail}>
                        <div className={styles.desc}>{t("gold.desc")}</div>
                        <Count value={data?.gold} />
                    </div>
                </div>
            </section>
            <section className={styles.raidian}>
                <SectionTitle data={parseColorString(t("raidian"))} />
                <div className={styles.content}>
                    <div className={styles.detail}>
                        <div className={styles.desc}>{t("raidian.desc")}</div>
                        <Count value={data?.raidian} />
                    </div>
                </div>
            </section>
        </div>
    )
}

export default ServerSummary
